import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PageHero } from "@/components/shared/PageHero";
import { ProjectCard } from "@/components/shared/ProjectCard";
import { CTA } from "@/components/home/CTA";
import { useProjects } from "@/hooks/useContent";
import { useSeo } from "@/components/shared/useSeo";

const HERO = "https://images.unsplash.com/photo-1509390144018-eeaf65052242?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const FILTERS = ["Tümü", "Elektrik", "Otomasyon", "Taahhüt", "Pano", "Mühendislik"];

export default function Projeler() {
  const { data: projects = [] } = useProjects();
  const [filter, setFilter] = useState("Tümü");
  useSeo({
    title: "Projeler | Sistem Elektrik ve Mühendislik",
    description: "Endüstriyel elektrik, otomasyon ve mühendislik hizmet alanlarını temsil eden örnek proje senaryoları.",
    canonical: "https://sistemmuhendislik.com/projeler",
  });

  const filtered = useMemo(
    () => (filter === "Tümü" ? projects : projects.filter((p) => p.category === filter)),
    [filter, projects]
  );

  return (
    <>
      <PageHero
        eyebrow="PROJELER"
        title="Mühendisliği sahada gerçeğe dönüştürüyoruz."
        subtitle="Elektrik, otomasyon ve mühendislik hizmet alanlarımızı temsil eden örnek proje senaryoları."
        image={HERO}
        crumb={[{ label: "Projeler" }]}
      />

      <section className="bg-light py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="mb-10 border-l-2 border-cyan bg-white px-5 py-4 text-sm leading-relaxed text-ink/65">
            Bu bölümdeki kartlar, sunduğumuz hizmet alanlarını görsel olarak anlatan <strong className="text-ink/80">örnek proje senaryolarıdır</strong>. Gerçek referans projeler, paylaşım onayları ve doğrulanmış proje bilgileri hazır olduğunda ayrıca eklenecektir.
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3 mb-12">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-5 py-2.5 text-sm font-semibold border transition-colors duration-300 ${
                  filter === f ? "bg-navy text-white border-navy" : "bg-transparent text-ink/60 border-ink/15 hover:border-tech hover:text-tech"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filtered.map((p, i) => (
                <motion.div key={p.slug} layout
                  initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.4 }}>
                  <ProjectCard project={p} index={i} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {filtered.length === 0 && (
            <p className="text-ink/50 py-16 text-center">Bu kategoride henüz proje bulunmuyor.</p>
          )}
        </div>
      </section>

      <CTA />
    </>
  );
}
